import { supabase } from './supabase'

export interface FounderAskAuthor {
  id: string
  first_name: string
  last_name: string
  username: string | null
  avatar_url: string | null
  is_founder?: boolean
}

export interface FounderAsk {
  id: string
  user_id: string
  question: string
  detail: string | null
  directed_to: string | null
  reply_count: number
  last_reply_at: string | null
  first_answered_at: string | null
  is_hidden: boolean
  created_at: string
  updated_at: string
}

// A resolved link to existing platform content (contribution or curation).
export interface FounderAskAttachmentRef {
  type: 'contribution' | 'curation'
  id: string
  title: string | null
  imageUrl: string | null
  slug: string | null
}

export interface FounderAskReply {
  id: string
  ask_id: string
  user_id: string
  parent_reply_id: string | null
  content: string
  attachment_type?: 'contribution' | 'curation' | null
  attachment_id?: string | null
  created_at: string
  author_is_founder: boolean
  user?: FounderAskAuthor
}

export interface FounderAskThread {
  ask: FounderAsk & {
    author?: FounderAskAuthor
    attachments: FounderAskAttachmentRef[]
    is_answered: boolean
  }
  replies: FounderAskReply[]
}

export interface DiscussFeedRow {
  discussionType: 'contribution' | 'curation' | 'founder_ask'
  targetId: string
  title: string
  slug: string | null
  imageUrl: string | null
  ownerId: string
  ownerFirstName: string
  ownerLastName: string
  ownerUsername: string | null
  ownerAvatarUrl: string | null
  lastCommentContent: string
  lastCommentAt: string
  commentCount: number
  userHasCommented: boolean
  isAnswered: boolean
  directedTo: string | null
}

export interface CreateAskInput {
  userId: string
  question: string
  detail?: string
  // Links to EXISTING platform content only (no uploads). Each is a contribution or curation.
  attachments?: { type: 'contribution' | 'curation'; id: string }[]
  directedTo?: 'founders'
}

// Founder replies pin to the top (chronological among themselves); community
// replies follow chronologically. The authority is the joined users.is_founder.
export function sortFounderAskReplies(replies: FounderAskReply[]): FounderAskReply[] {
  return replies.slice().sort((a, b) => {
    if (a.author_is_founder !== b.author_is_founder) return a.author_is_founder ? -1 : 1
    return a.created_at < b.created_at ? -1 : a.created_at > b.created_at ? 1 : 0
  })
}

// Resolve raw attachment rows into lightweight summaries for the thread view.
async function resolveAskAttachments(
  rows: { attachment_type: 'contribution' | 'curation'; attachment_id: string; position: number }[]
): Promise<FounderAskAttachmentRef[]> {
  if (rows.length === 0) return []

  const contributionIds = rows.filter(r => r.attachment_type === 'contribution').map(r => r.attachment_id)
  const curationIds = rows.filter(r => r.attachment_type === 'curation').map(r => r.attachment_id)

  const [contribRes, curationRes] = await Promise.all([
    contributionIds.length
      ? supabase.from('contributions').select('id, title, cached_image_url').in('id', contributionIds)
      : Promise.resolve({ data: [] as any[] }),
    curationIds.length
      ? supabase.from('issues').select('id, title, slug, highlighted_image_url').in('id', curationIds)
      : Promise.resolve({ data: [] as any[] }),
  ])

  const contribMap = new Map<string, any>((contribRes.data || []).map((c: any) => [c.id, c]))
  const curationMap = new Map<string, any>((curationRes.data || []).map((c: any) => [c.id, c]))

  return rows
    .slice()
    .sort((a, b) => a.position - b.position)
    .map((r): FounderAskAttachmentRef => {
      if (r.attachment_type === 'contribution') {
        const c = contribMap.get(r.attachment_id)
        return { type: 'contribution', id: r.attachment_id, title: c?.title ?? null, imageUrl: c?.cached_image_url ?? null, slug: null }
      }
      const c = curationMap.get(r.attachment_id)
      return { type: 'curation', id: r.attachment_id, title: c?.title ?? null, imageUrl: c?.highlighted_image_url ?? null, slug: c?.slug ?? null }
    })
}

export const founderAsksService = {
  async createAsk(input: CreateAskInput): Promise<{ id: string; created_at: string }> {
    const row: any = {
      user_id: input.userId,
      question: input.question.trim(),
    }
    if (input.detail) row.detail = input.detail.trim()
    if (input.directedTo) row.directed_to = input.directedTo

    const { data, error } = await supabase
      .from('founder_asks')
      .insert(row)
      .select('id, created_at')
      .single()

    if (error) {
      console.error('Error creating founder ask:', error)
      throw error
    }

    if (input.attachments && input.attachments.length > 0) {
      const attachmentRows = input.attachments.map((a, position) => ({
        ask_id: data.id,
        attachment_type: a.type,
        attachment_id: a.id,
        position,
      }))
      const { error: attachError } = await supabase.from('founder_ask_attachments').insert(attachmentRows)
      if (attachError) {
        console.error('Error attaching ask references:', attachError)
        throw attachError
      }
    }

    return { id: data.id, created_at: data.created_at }
  },

  async fetchAskThread(askId: string): Promise<FounderAskThread> {
    const { data: ask, error: askError } = await supabase
      .from('founder_asks')
      .select(`
        *,
        author:users!user_id(id, first_name, last_name, username, avatar_url, is_founder)
      `)
      .eq('id', askId)
      .single()

    if (askError) {
      console.error('Error fetching founder ask:', askError)
      throw askError
    }

    // Moderation guard: never surface a hidden ask on the thread view. The
    // authoritative gate is RLS (see the hide-rule migration); this is a
    // client-side backstop for the window before that DDL is applied. Callers
    // (thread view, context pane, direct-visit synthetic thread) all treat a
    // rejection as "not available" and degrade gracefully.
    if ((ask as any).is_hidden) {
      throw new Error('This question is no longer available')
    }

    const { data: rawReplies, error: repliesError } = await supabase
      .from('founder_ask_replies')
      .select(`
        id, ask_id, user_id, parent_reply_id, content, attachment_type, attachment_id, created_at,
        user:users!user_id(id, first_name, last_name, username, avatar_url, is_founder)
      `)
      .eq('ask_id', askId)
      .eq('is_hidden', false)

    if (repliesError) {
      console.error('Error fetching ask replies:', repliesError)
      throw repliesError
    }

    // Ask-level attachments only. The iOS client stores reply attachments in
    // this same table with reply_id set (a column added out-of-band, not in the
    // committed schema), so ask-level rows must be scoped to reply_id IS NULL —
    // otherwise mobile reply attachments leak into the ask block at the top.
    const { data: rawAttachments, error: attachError } = await supabase
      .from('founder_ask_attachments')
      .select('id, attachment_type, attachment_id, position')
      .eq('ask_id', askId)
      .is('reply_id', null)
      .order('position')

    if (attachError) {
      console.error('Error fetching ask attachments:', attachError)
      throw attachError
    }

    const attachments = await resolveAskAttachments(rawAttachments || [])

    // Reply attachments have two storage locations: web writes the inline
    // columns on founder_ask_replies; iOS writes founder_ask_attachments rows
    // keyed by reply_id. Read the table too and backfill the inline fields when
    // empty, so a reply attachment created on either client renders inline.
    const replyIds = (rawReplies || []).map((r: any) => r.id)
    const replyAttachmentByReplyId = new Map<string, { attachment_type: string; attachment_id: string }>()
    if (replyIds.length > 0) {
      const { data: replyAttRows, error: replyAttError } = await supabase
        .from('founder_ask_attachments')
        .select('reply_id, attachment_type, attachment_id, position')
        .in('reply_id', replyIds)
        .order('position')
      if (replyAttError) {
        // Non-fatal: fall back to inline columns only (don't break the thread).
        console.error('Error fetching reply attachments:', replyAttError)
      } else {
        for (const row of replyAttRows || []) {
          if (row.reply_id && !replyAttachmentByReplyId.has(row.reply_id)) {
            replyAttachmentByReplyId.set(row.reply_id, {
              attachment_type: row.attachment_type,
              attachment_id: row.attachment_id,
            })
          }
        }
      }
    }

    const replies: FounderAskReply[] = sortFounderAskReplies(
      (rawReplies || []).map((r: any) => {
        const user = Array.isArray(r.user) ? r.user[0] : r.user
        const fallback = replyAttachmentByReplyId.get(r.id)
        return {
          ...r,
          // Prefer the web inline columns; fall back to the iOS table row.
          attachment_type: r.attachment_type ?? fallback?.attachment_type ?? null,
          attachment_id: r.attachment_id ?? fallback?.attachment_id ?? null,
          user,
          author_is_founder: !!user?.is_founder,
        }
      })
    )

    const author = Array.isArray((ask as any).author) ? (ask as any).author[0] : (ask as any).author

    return {
      ask: {
        ...(ask as any),
        author,
        attachments,
        is_answered: !!(ask as any).first_answered_at,
      },
      replies,
    }
  },

  // All platform founders, for the context pane's anchored founders block
  // (founders render there whether or not they've replied).
  async fetchFounders(): Promise<FounderAskAuthor[]> {
    const { data, error } = await supabase
      .from('users')
      .select('id, first_name, last_name, username, avatar_url, is_founder')
      .eq('is_founder', true)

    if (error) {
      console.error('Error fetching founders:', error)
      throw error
    }
    return data || []
  },

  async postReply(input: {
    askId: string
    userId: string
    content: string
    parentReplyId?: string
    attachment?: { type: 'contribution' | 'curation'; id: string } | null
  }): Promise<FounderAskReply> {
    const row: any = {
      ask_id: input.askId,
      user_id: input.userId,
      content: input.content.trim(),
    }
    if (input.parentReplyId) row.parent_reply_id = input.parentReplyId
    if (input.attachment) {
      row.attachment_type = input.attachment.type
      row.attachment_id = input.attachment.id
    }

    const { data, error } = await supabase
      .from('founder_ask_replies')
      .insert(row)
      .select(`
        id, ask_id, user_id, parent_reply_id, content, attachment_type, attachment_id, created_at,
        user:users!user_id(id, first_name, last_name, username, avatar_url, is_founder)
      `)
      .single()

    if (error) {
      console.error('Error posting reply:', error)
      throw error
    }

    const user = Array.isArray((data as any).user) ? (data as any).user[0] : (data as any).user
    return { ...(data as any), user, author_is_founder: !!user?.is_founder }
  },

  // Owner-editable columns only (question/detail); everything else is
  // trigger-owned and rejected by column grants.
  async updateAsk(input: { askId: string; question: string; detail?: string }): Promise<void> {
    const { error } = await supabase
      .from('founder_asks')
      .update({ question: input.question.trim(), detail: input.detail?.trim() || null })
      .eq('id', input.askId)

    if (error) {
      console.error('Error updating founder ask:', error)
      throw error
    }
  },

  async deleteAsk(askId: string): Promise<void> {
    const { error } = await supabase.from('founder_asks').delete().eq('id', askId)
    if (error) {
      console.error('Error deleting founder ask:', error)
      throw error
    }
  },

  async deleteReply(replyId: string): Promise<void> {
    const { error } = await supabase.from('founder_ask_replies').delete().eq('id', replyId)
    if (error) {
      console.error('Error deleting ask reply:', error)
      throw error
    }
  },

  // Realtime INSERTs on this ask's replies; mirrors discussionsService.subscribe
  // (the realtime payload has no joins, so re-fetch the full row).
  subscribeToReplies(askId: string, callback: (reply: FounderAskReply) => void) {
    return supabase
      .channel(`founder_ask_replies:${askId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'founder_ask_replies',
          filter: `ask_id=eq.${askId}`,
        },
        async (payload: any) => {
          const { data } = await supabase
            .from('founder_ask_replies')
            .select(`
              id, ask_id, user_id, parent_reply_id, content, attachment_type, attachment_id, created_at,
              user:users!user_id(id, first_name, last_name, username, avatar_url, is_founder)
            `)
            .eq('id', payload.new.id)
            .single()

          if (data) {
            const user = Array.isArray((data as any).user) ? (data as any).user[0] : (data as any).user
            callback({ ...(data as any), user, author_is_founder: !!user?.is_founder })
          }
        }
      )
      .subscribe()
  },

  async fetchDiscussFeed(options?: {
    type?: 'all' | 'items' | 'curations' | 'founder_asks'
    myActivity?: boolean
    limit?: number
    offset?: number
  }): Promise<DiscussFeedRow[]> {
    const { data, error } = await supabase.rpc('get_user_discussion_threads_filtered', {
      p_type: options?.type ?? 'all',
      p_sort: 'recent',
      p_category_id: null,
      p_subcategory_id: null,
      p_my_activity: options?.myActivity ?? false,
      p_search: null,
      p_limit: options?.limit ?? 50,
      p_offset: options?.offset ?? 0,
    })

    if (error) {
      console.error('Error fetching discuss feed:', error)
      throw error
    }

    return (data || []).map((row: any) => ({
      discussionType: row.discussion_type,
      targetId: row.target_id,
      title: row.title,
      slug: row.slug,
      imageUrl: row.image_url,
      ownerId: row.owner_id,
      ownerFirstName: row.owner_first_name,
      ownerLastName: row.owner_last_name || '',
      ownerUsername: row.owner_username,
      ownerAvatarUrl: row.owner_avatar_url,
      lastCommentContent: row.last_comment_content,
      lastCommentAt: row.last_comment_at,
      commentCount: Number(row.comment_count),
      userHasCommented: !!row.user_has_commented,
      isAnswered: !!row.is_answered,
      directedTo: row.directed_to || null,
    }))
  },
}
